import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';
import { MdLanguage } from 'react-icons/md';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location]);

  const navLinks = [
    { path: '/', label: t('header.home') },
    { path: '/about', label: t('header.about') },
    { path: '/projects', label: t('header.projects') },
    { path: '/gallery', label: t('header.gallery') },
    { path: '/cv', label: t('header.cv') },
    { path: '/contact', label: t('header.contact') },
  ];

  return (
    <HeaderContainer isScrolled={isScrolled}>
      <div className="container">
        <HeaderContent>
          <Logo to="/">
            <span>Bartłomiej Zawadzki</span>
          </Logo>

          <MobileMenuButton onClick={toggleMenu}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>

          <Nav isOpen={isMenuOpen}>
            <NavList>
              {navLinks.map((link) => (
                <NavItem key={link.path}>
                  <NavLink 
                    to={link.path} 
                    className={location.pathname === link.path ? 'active' : ''}
                  >
                    {link.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavList>
          </Nav>

          <LanguageSelector>
            <LanguageButton onClick={toggleLanguageMenu}>
              <MdLanguage />
              <span>{i18n.language === 'pl' ? 'PL' : 'EN'}</span>
            </LanguageButton>
            <LanguageMenu isOpen={isLanguageMenuOpen}>
              <LanguageOption 
                onClick={() => changeLanguage('pl')}
                className={i18n.language === 'pl' ? 'active' : ''}
              >
                {t('language.pl')}
              </LanguageOption>
              <LanguageOption 
                onClick={() => changeLanguage('en')}
                className={i18n.language === 'en' ? 'active' : ''}
              >
                {t('language.en')}
              </LanguageOption>
            </LanguageMenu>
          </LanguageSelector>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--header-height);
  background-color: transparent;
  transition: all var(--transition-normal) ease;
  z-index: 1000;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(10, 10, 10, 0.5); /* Semi-transparent dark bg */
    backdrop-filter: blur(10px);
    z-index: -1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const Logo = styled(Link)`
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  
  span {
    background: linear-gradient(to right, var(--primary-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
    order: 3;
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    position: fixed;
    top: var(--header-height);
    left: 0;
    width: 100%;
    height: ${props => props.isOpen ? 'calc(100vh - var(--header-height))' : '0'};
    background-color: var(--white);
    overflow: hidden;
    transition: height var(--transition-normal) ease;
    z-index: 999;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem 0;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    margin: 0;
    text-align: center;
  }
`;

const NavLink = styled(Link)`
  display: block;
  padding: 0.5rem;
  color: var(--text-color);
  font-weight: 500;
  transition: color var(--transition-fast) ease;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--primary-color);
    transition: width var(--transition-normal) ease;
  }
  
  &:hover, &.active {
    color: var(--primary-color);
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    &:after {
      display: none;
    }
    
    &:hover, &.active {
      background-color: var(--gray-100);
    }
  }
`;

const LanguageSelector = styled.div`
  position: relative;
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  padding: 0.5rem;
  color: var(--text-color);
  font-size: 0.875rem;
  
  svg {
    margin-right: 0.25rem;
  }
  
  &:hover {
    background-color: var(--gray-100);
  }
`;

const LanguageMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 150px;
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0.5rem)' : 'translateY(0)'};
  transition: all var(--transition-fast) ease;
  z-index: 1001;
`;

const LanguageOption = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  background: none;
  border: none;
  color: var(--text-color);
  
  &:hover, &.active {
    background-color: var(--gray-100);
    color: var(--primary-color);
  }
  
  &.active {
    font-weight: 600;
  }
`;

export default Header;